import {
	createContext,
	useEffect,
	useState,
} from "react";
import { useSelector } from "react-redux";
import PageContent from "../../common/components/PageContent";
import PageTitle from "../../common/components/PageTitle/PageTitle";
import {
	selectUserIdentity,
} from "../authentication/auth.selector";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { PrimaryButton } from "../../common/components/CustomButton";
import { useLazyGetSnoopEDeviceCodeQuery, useLazyGetSnoopETokenQuery } from "./greenAlignApi";
import { format } from "date-fns";


interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ p: 3 }}>
					<Typography component={"div"}>{children}</Typography>
				</Box>
			)}
		</div>
	);
}

interface IGreenAlignPageContext {
}

export const GreenAlignPageContext = createContext<IGreenAlignPageContext | null>(null);

function GreenAlignPage() {
	const [canStartNewRegistration, setCanStartNewRegistration] = useState(true);
	const [tokenPollingInterval, setTokenPollingInterval] = useState(0);
	const identity = useSelector(selectUserIdentity);
	const [userRegistrationExpiryDate, setUserRegistrationExpiryDate] = useState<Date | undefined>();

	useEffect(() => {
		if (identity?.snoopy_token_timestamp) {
			setUserRegistrationExpiryDate(new Date(identity.snoopy_token_timestamp * 1000));
		} else {
			setUserRegistrationExpiryDate(undefined);
		}
	}, [identity]);

	const [activeTab, setActiveTab] = useState(0);
	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setActiveTab(newValue);
	};

	const [triggerTokenFetching, resultTokenFetching] = useLazyGetSnoopETokenQuery({
    	pollingInterval: tokenPollingInterval
	});
	const [triggerDeviceCodeFetching, resultDeviceCodeFetching] = useLazyGetSnoopEDeviceCodeQuery({});

	useEffect(() => {
		let interval = undefined;
		if (resultDeviceCodeFetching?.data?.device_code && resultDeviceCodeFetching?.data?.expires_in && resultDeviceCodeFetching?.data?.interval) {
			setTokenPollingInterval(resultDeviceCodeFetching.data.interval * 1000)
			triggerTokenFetching(resultDeviceCodeFetching.data.device_code);
			interval = setInterval(() => {
			   setTokenPollingInterval(0);
			   setCanStartNewRegistration(true);
  			}, resultDeviceCodeFetching.data.expires_in * 1000);
		}

		return () => {
			if (interval) {
				clearInterval(interval);
			}
		}
	}, [resultDeviceCodeFetching])


	useEffect(() => {
		if (resultTokenFetching?.data) {
			if (resultTokenFetching.data.status == "authorization_complete") {
				setTokenPollingInterval(0);
				const expiryDate = new Date(resultTokenFetching.data.Expires_at * 1000);
			   	setUserRegistrationExpiryDate(expiryDate);
				setCanStartNewRegistration(true);
				setTokenPollingInterval(0);
			}
		}
	}, [resultTokenFetching]);

	return (
		<>
			<PageTitle
				label="Green align"
			/>
			<GreenAlignPageContext.Provider
				value={{ }}
			>
				<PageContent>
					<Box
						sx={{
							borderBottom: 1,
							borderColor: "divider",
						}}
					>
						<Tabs
							value={activeTab}
							onChange={handleTabChange}
							aria-label="green align tab"
						>
							<Tab label="Main" />
							<Tab
								label={`Register user ${identity?.gaia_id} to Snoop-e`}
								disabled={false}
							/>
						</Tabs>
					</Box>
					<CustomTabPanel
						value={activeTab}
						index={0}>
						Content
					</CustomTabPanel>
					<CustomTabPanel
						value={activeTab}
						index={1}>
						<PrimaryButton
							text={`Retrieve registration link for user ${identity?.gaia_id}`}
							type="button"
							color="primary"
							isDisabled={!canStartNewRegistration}
							sx={{ marginRight: "16px", width: 300 }}
							onClick={() => {
								setCanStartNewRegistration(false);
								triggerDeviceCodeFetching({});
							}}
						></PrimaryButton>
						{(!canStartNewRegistration && resultDeviceCodeFetching?.data) &&
							<>
								<Box>
									Use code {resultDeviceCodeFetching.data.user_code}&nbsp;<a href={resultDeviceCodeFetching.data.verification_uri} target="_blank">here</a>
								</Box>
								<Box>{resultTokenFetching?.data?.status}</Box>
							</>
						}
						{
							(userRegistrationExpiryDate) && <Box>
								Registered until: {format(userRegistrationExpiryDate, "dd/MM/yyyy HH:mm")}
							</Box>
						}
						{
							(resultTokenFetching?.data?.token) && <Box>
								token: {resultTokenFetching?.data?.token}
							</Box>
						}
					</CustomTabPanel>
				</PageContent>
			</GreenAlignPageContext.Provider>
		</>
	);
}

export default GreenAlignPage;
